
import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useLanguage();
  
  return (
    <footer className="border-t border-border bg-muted/30 py-10 md:py-14">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 md:gap-10">
          {/* Brand */}
          <div className="sm:col-span-2">
            <Link to="/" className="flex items-center space-x-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-base">D</span>
              </div>
              <span className="text-lg font-semibold tracking-tight">DCFlow</span>
            </Link>
            <p className="text-muted-foreground mb-5 max-w-sm text-sm leading-relaxed">
              {t('footer.description')}
            </p>
            <a 
              href="mailto:insightechpro@gmail.com" 
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-200" 
              aria-label="insightechpro@gmail.com"
            >
              <Mail size={16} />
              insightechpro@gmail.com
            </a>
          </div>
          
          {/* Links */}
          <div>
            <h3 className="text-sm font-semibold mb-4 text-foreground">{t('footer.quickLinks')}</h3>
            <ul className="space-y-2.5">
              {[
                { to: '/', label: t('nav.home') },
                { to: '/dashboard', label: t('nav.dashboard') },
                { to: '/tools', label: t('nav.tools') },
                { to: '/about', label: t('nav.about') },
                { to: '/privacy', label: t('footer.privacy') },
                { to: '/terms', label: t('footer.terms') },
              ].map(({ to, label }) => (
                <li key={to}>
                  <Link to={to} className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Contact */}
          <div>
            <h3 className="text-sm font-semibold mb-4 text-foreground">{t('footer.contact')}</h3>
            <ul className="space-y-3">
              <li>
                <span className="block text-xs text-muted-foreground mb-0.5">{t('footer.email')}</span>
                <a href="mailto:insightechpro@gmail.com" className="text-sm text-foreground hover:text-primary transition-colors duration-200 break-all">
                  insightechpro@gmail.com
                </a>
              </li>
              <li>
                <span className="block text-xs text-muted-foreground mb-0.5">{t('footer.address')}</span>
                <address className="not-italic text-sm text-foreground">
                  {t('footer.location')}
                </address>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-border mt-10 pt-6 flex flex-col md:flex-row justify-between items-center gap-3">
          <p className="text-xs text-muted-foreground">
            &copy; {currentYear} DCFlow. {t('footer.rights')}
          </p>
          <div className="flex gap-5">
            {[
              { to: '/privacy', label: t('footer.privacy') },
              { to: '/terms', label: t('footer.terms') },
              { to: '/cookies', label: t('footer.cookies') },
            ].map(({ to, label }) => (
              <Link key={to} to={to} className="text-xs text-muted-foreground hover:text-foreground transition-colors duration-200">
                {label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
